import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import { DeliveryFeePopup } from "@/components/orders/DeliveryFeePopup";
import { Building, Check, Truck } from "lucide-react";
import { useTransport } from "@/hooks/useTransport";
import { useListingManagement } from "@/hooks/useListingManagement";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getTransportStatusLabel } from "@/utils/listingUtils";
import { Transport } from "@/types/transport";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { UserManagementService } from "@/services/user/userManagementService";
import {
  normalizeDeliveryAddress,
  type NormalizedUserDeliveryAddress,
} from "@/selectors/deliveryAddressSelectors";

interface ListingDetailsDeliveryProps {
  transports: Transport[];
  deliveryFee?: {
    zoneA: number;
    zoneB: number;
    zoneC: number;
    zoneD: number;
  } | null;
  listingId: string;
}

export const ListingDetailsDelivery: React.FC<ListingDetailsDeliveryProps> = ({
  transports,
  deliveryFee,
  listingId,
}) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  // Form state - separated into two forms
  type SellerTransportStatus =
    | "not_started"
    | "planned"
    | "in_progress"
    | "completed";
  const [sellerTransportForm, setSellerTransportForm] = useState<{
    status: SellerTransportStatus;
    fromAddress: string;
    comment: string;
    scheduledDate: string;
  }>({
    status: "not_started",
    fromAddress: "",
    comment: "",
    scheduledDate: "",
  });

  // Unified buyer transport form state
  type BuyerTransportStatus =
    | "not_started"
    | "requested"
    | "planned"
    | "in_progress"
    | "completed";
  const [buyerTransportForm, setBuyerTransportForm] = useState<{
    transportType: "pickup" | "delivery";
    status: BuyerTransportStatus;
    comment: string;
    scheduledDate: string;
    address: string;
  }>({
    transportType: "delivery",
    status: "not_started",
    comment: "",
    scheduledDate: "",
    address: "", // Only used/displayed when transportType === 'delivery'
  });

  // Track if user has manually selected a transport type
  const [hasManuallySelectedType, setHasManuallySelectedType] = useState(false);

  // Filter transports by type
  const sellerPickupTransports = transports.filter(
    (t) => t.type === "seller_pickup",
  );

  // Find the single buyer transport (either BUYER_PICKUP or BUYER_DELIVERY)
  const buyerTransport = transports.find(
    (t) => t.type === "buyer_pickup" || t.type === "buyer_delivery",
  );

  // Transport mutations
  const {
    updateTransportAsync: updateTransport,
    createTransportAsync: createTransport,
    isUpdating: isUpdatingTransport,
  } = useTransport();

  // Listing mutations for updating delivery fees
  const {
    updateListing,
    getListingDetails,
    confirmBuyerDeliveryAsync,
    isConfirmingBuyerDelivery,
  } = useListingManagement();
  const queryClient = useQueryClient();

  // Get listing details
  const { data: listing, refetch: refetchListingDetails } =
    getListingDetails(listingId);

  type DeliveryAddress = NormalizedUserDeliveryAddress;

  const [sellerAddresses, setSellerAddresses] = useState<DeliveryAddress[]>([]);
  const [buyerAddresses, setBuyerAddresses] = useState<DeliveryAddress[]>([]);
  const [isLoadingSellerAddresses, setIsLoadingSellerAddresses] =
    useState(false);
  const [isLoadingBuyerAddresses, setIsLoadingBuyerAddresses] = useState(false);

  const formatDeliveryAddress = useCallback((a: DeliveryAddress) => {
    return [a.street, `${a.postalCode} ${a.city}`.trim(), a.country]
      .filter(Boolean)
      .join(", ");
  }, []);

  const getDefaultAddress = useCallback((arr: DeliveryAddress[]) => {
    return arr.find((a) => a.isDefault) ?? arr[0];
  }, []);

  const resolvedSellerPickupLine = useMemo(() => {
    const trimmed = sellerTransportForm.fromAddress?.trim();
    if (trimmed) return trimmed;
    const def = getDefaultAddress(sellerAddresses);
    return def ? formatDeliveryAddress(def) : "";
  }, [
    sellerTransportForm.fromAddress,
    sellerAddresses,
    getDefaultAddress,
    formatDeliveryAddress,
  ]);

  // Loading states
  const [isUpdatingSellerPickup, setIsUpdatingSellerPickup] = useState(false);
  const [isUpdatingBuyerTransport, setIsUpdatingBuyerTransport] =
    useState(false);

  // Delivery fees state
  const [deliveryFees, setDeliveryFees] = useState(null);
  const [isUpdatingDeliveryFees, setIsUpdatingDeliveryFees] = useState(false);

  // Initialize delivery fees from prop
  useEffect(() => {
    if (deliveryFee) {
      setDeliveryFees(deliveryFee);
    }
  }, [deliveryFee]);

  // Track if user is actively editing to prevent form reset
  const [isEditingSellerForm, setIsEditingSellerForm] = useState(false);
  const [isEditingBuyerForm, setIsEditingBuyerForm] = useState(false);

  // Fetch seller/buyer delivery addresses when listing is sold
  useEffect(() => {
    const listingData = listing?.listing;
    if (!listingData) return;
    if (listingData.status !== "completed") return;

    // Important: sellerId/winnerId in listings are often ACTOR ids.
    // Delivery addresses are stored per USER id, which we have on seller/winner objects.
    const sellerUserId = listingData.seller?._id || listingData.sellerId;
    const buyerUserId = listingData.winner?._id || listingData.winnerId;

    (async () => {
      if (sellerUserId) {
        setIsLoadingSellerAddresses(true);
        try {
          const data =
            await UserManagementService.getUserDeliveryAddresses(sellerUserId);
          const normalized = (Array.isArray(data) ? data : [])
            .map(normalizeDeliveryAddress)
            .filter(Boolean) as DeliveryAddress[];
          setSellerAddresses(normalized);
        } catch (err) {
          setSellerAddresses([]);
        } finally {
          setIsLoadingSellerAddresses(false);
        }
      }

      if (buyerUserId) {
        setIsLoadingBuyerAddresses(true);
        try {
          const data =
            await UserManagementService.getUserDeliveryAddresses(buyerUserId);
          const normalized = (Array.isArray(data) ? data : [])
            .map(normalizeDeliveryAddress)
            .filter(Boolean) as DeliveryAddress[];
          setBuyerAddresses(normalized);
        } catch (err) {
          setBuyerAddresses([]);
        } finally {
          setIsLoadingBuyerAddresses(false);
        }
      }
    })();
  }, [listing?.listing]);

  // Prefill pickup/delivery address from defaults (only if user hasn't started editing)
  useEffect(() => {
    if (isEditingSellerForm) return;
    if (!listing?.listing || listing.listing.status !== "completed") return;
    if (sellerTransportForm.fromAddress) return;
    if (sellerAddresses.length === 0) return;

    const def = getDefaultAddress(sellerAddresses);
    if (!def) return;
    setSellerTransportForm((prev) => ({
      ...prev,
      fromAddress: formatDeliveryAddress(def),
    }));
  }, [
    sellerAddresses,
    listing?.listing,
    isEditingSellerForm,
    sellerTransportForm.fromAddress,
    formatDeliveryAddress,
    getDefaultAddress,
  ]);

  useEffect(() => {
    if (isEditingBuyerForm) return;
    if (!listing?.listing || listing.listing.status !== "completed") return;
    if (buyerTransportForm.transportType !== "delivery") return;
    if (buyerTransportForm.address) return;
    if (buyerAddresses.length === 0) return;

    const def = getDefaultAddress(buyerAddresses);
    if (!def) return;
    setBuyerTransportForm((prev) => ({
      ...prev,
      address: formatDeliveryAddress(def),
    }));
  }, [
    buyerAddresses,
    listing?.listing,
    isEditingBuyerForm,
    buyerTransportForm.transportType,
    buyerTransportForm.address,
    formatDeliveryAddress,
    getDefaultAddress,
  ]);

  // Initialize form data when transports are loaded (only if not currently editing)
  useEffect(() => {
    if (
      sellerPickupTransports &&
      sellerPickupTransports.length > 0 &&
      !isEditingSellerForm
    ) {
      const transport = sellerPickupTransports[0];
      // Filter out "requested" status for seller transports (only buyer transports can be "requested")
      const sellerStatus: SellerTransportStatus =
        transport.status === "requested"
          ? "not_started"
          : (transport.status as SellerTransportStatus);
      setSellerTransportForm({
        status: sellerStatus,
        fromAddress: transport.fromAddress || "",
        comment: transport.comment || "",
        scheduledDate: transport.scheduledDate || "",
      });
    }
  }, [sellerPickupTransports, isEditingSellerForm]);

  // Initialize buyer transport form from existing transport (only on initial load)
  useEffect(() => {
    // Don't run if user has manually selected a type or is editing
    if (hasManuallySelectedType || isEditingBuyerForm) return;

    if (buyerTransport) {
      setBuyerTransportForm({
        transportType:
          buyerTransport.type === "buyer_pickup" ? "pickup" : "delivery",
        status: buyerTransport.status,
        comment: buyerTransport.comment || "",
        scheduledDate: buyerTransport.scheduledDate || "",
        address:
          buyerTransport.type === "buyer_delivery"
            ? buyerTransport.toAddress || ""
            : "",
      });
      setHasManuallySelectedType(false); // Reset so it can initialize
    }
  }, [buyerTransport, hasManuallySelectedType, isEditingBuyerForm]);

  // Validation functions
  const hasSellerPickupChanges = useMemo(() => {
    if (!sellerPickupTransports || sellerPickupTransports.length === 0)
      return false;
    const original = sellerPickupTransports[0];
    return (
      sellerTransportForm.status !== original.status ||
      sellerTransportForm.comment !== (original.comment || "") ||
      sellerTransportForm.fromAddress !== (original.fromAddress || "") ||
      sellerTransportForm.scheduledDate !== (original.scheduledDate || "")
    );
  }, [sellerPickupTransports, sellerTransportForm]);

  // Unified buyer transport validation
  const hasBuyerTransportChanges = useMemo(() => {
    if (!buyerTransport) {
      // If no existing transport, check if form has any data
      return (
        buyerTransportForm.status !== "not_started" ||
        buyerTransportForm.comment !== "" ||
        buyerTransportForm.scheduledDate !== "" ||
        buyerTransportForm.address !== ""
      );
    }

    const original = buyerTransport;
    const originalTransportType =
      original.type === "buyer_pickup" ? "pickup" : "delivery";

    return (
      buyerTransportForm.transportType !== originalTransportType ||
      buyerTransportForm.status !== original.status ||
      buyerTransportForm.comment !== (original.comment || "") ||
      buyerTransportForm.scheduledDate !== (original.scheduledDate || "") ||
      (buyerTransportForm.transportType === "delivery" &&
        buyerTransportForm.address !== (original.toAddress || ""))
    );
  }, [buyerTransport, buyerTransportForm]);

  // Seller pickup is always valid (no required fields)
  const isSellerPickupValid = true;

  // Buyer transport is always valid (no required fields)
  const isBuyerTransportValid = true;

  // Save handlers
  const handleSaveSellerPickup = async () => {
    if (!sellerPickupTransports || sellerPickupTransports.length === 0) {
      toast.error(L.noSellerPickupFound ?? "Ingen säljar-upphämtning hittades");
      return;
    }

    const transport = sellerPickupTransports[0];
    setIsUpdatingSellerPickup(true);

    try {
      await updateTransport({
        id: transport.id,
        transportId: String(transport.id),
        listingId: listingId,
        type: "seller_pickup",
        status: sellerTransportForm.status,
        comment: sellerTransportForm.comment,
        scheduledDate: sellerTransportForm.scheduledDate || undefined,
        address: sellerTransportForm.fromAddress || null,
      });

      // Invalidate and refetch listing details to update UI
      queryClient.invalidateQueries({
        queryKey: ["admin-listing-details", listingId],
      });
      await refetchListingDetails();

      setIsEditingSellerForm(false);
      toast.success(L.sellerPickupUpdated ?? "Säljar-upphämtning uppdaterad!");
    } catch (error) {
      toast.error(
        L.errorUpdatingSellerPickup ??
          "Kunde inte uppdatera säljar-upphämtning",
      );
    } finally {
      setIsUpdatingSellerPickup(false);
    }
  };

  // Unified buyer transport save handler
  const handleSaveBuyerTransport = async () => {
    // Validate date if provided
    if (buyerTransportForm.scheduledDate) {
      const deliveryDate = new Date(buyerTransportForm.scheduledDate);
      const now = new Date();
      const minDate = new Date(now);
      // Admin may book from tomorrow
      minDate.setDate(minDate.getDate() + 1);
      minDate.setHours(0, 0, 0, 0);

      if (deliveryDate < minDate) {
        toast.error(
          L.deliveryDateMin1Day?.split(".")[0] ??
            "Leveransdatum måste vara minst 1 dag framåt",
        );
        return;
      }
    }

    const transportType: "buyer_pickup" | "buyer_delivery" =
      buyerTransportForm.transportType === "pickup"
        ? "buyer_pickup"
        : "buyer_delivery";

    if (
      !buyerTransport &&
      transportType === "buyer_pickup" &&
      !resolvedSellerPickupLine.trim()
    ) {
      toast.error(
        L.sellerPickupForBuyerMissing ??
          "Säljarens upphämtningsadress saknas. Fyll i under upphämtning från säljare eller säkerställ att säljaren har en leveransadress.",
      );
      return;
    }

    setIsUpdatingBuyerTransport(true);

    try {
      // Use buyer delivery API endpoint (works for both create and update)
      const updateData: {
        listingId: string;
        type: "buyer_pickup" | "buyer_delivery";
        scheduledDate?: string;
        comment?: string;
        address?: string;
        status?: string;
      } = {
        listingId: listingId,
        type: transportType,
        scheduledDate: buyerTransportForm.scheduledDate || undefined,
        comment: buyerTransportForm.comment || undefined,
        status: buyerTransportForm.status || undefined,
      };

      // Address handling:
      // - If pickup: no address needed (warehouse address)
      // - If delivery: address from form
      if (
        buyerTransportForm.transportType === "delivery" &&
        buyerTransportForm.address
      ) {
        updateData.address = buyerTransportForm.address;
      }

      // Use create or update based on whether transport exists
      // Admin can update buyer transport, so use admin endpoint
      if (buyerTransport) {
        // Update existing transport (admin endpoint)
        await updateTransport(
          {
            ...updateData,
            id: buyerTransport.id,
            status:
              (buyerTransportForm.status as
                | "not_started"
                | "requested"
                | "planned"
                | "in_progress"
                | "completed") || undefined,
          },
          true,
        );
      } else {
        // Create new transport (admin endpoint)
        const sellerLine = resolvedSellerPickupLine.trim();
        const createData = {
          listingId: listingId,
          type: transportType,
          fromAddress:
            transportType === "buyer_pickup"
              ? sellerLine
              : "Bilenia Lager, Hägerstensvägen 123, 12345 Stockholm",
          toAddress:
            buyerTransportForm.transportType === "delivery"
              ? buyerTransportForm.address || ""
              : sellerLine,
          status: buyerTransportForm.status || undefined,
          scheduledDate: buyerTransportForm.scheduledDate || undefined,
          comment: buyerTransportForm.comment || undefined,
        };
        await createTransport(createData, true);
      }

      // Invalidate and refetch listing details to update UI
      queryClient.invalidateQueries({
        queryKey: ["admin-listing-details", listingId],
      });
      await refetchListingDetails();

      // Reset editing flag after successful update
      setIsEditingBuyerForm(false);

      toast.success(
        buyerTransport
          ? buyerTransportForm.transportType === "pickup"
            ? (L.buyerPickupUpdated ?? "Köparupphämtning uppdaterad!")
            : (L.buyerDeliveryUpdated ?? "Köparleverans uppdaterad!")
          : buyerTransportForm.transportType === "pickup"
            ? (L.buyerPickupBooked ?? "Köparupphämtning bokad!")
            : (L.buyerDeliveryBooked ?? "Köparleverans bokad!"),
      );
    } catch (error: unknown) {
      const errMsg =
        error instanceof Error
          ? error.message
          : typeof error === "object" &&
              error !== null &&
              "message" in error &&
              typeof (error as { message: unknown }).message === "string"
            ? (error as { message: string }).message
            : "";
      const responseData =
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { data?: unknown } }).response ===
          "object" &&
        (error as { response?: { data?: unknown } }).response != null
          ? (error as { response: { data?: unknown } }).response.data
          : undefined;
      toast.error(
        `${L.errorSaving ?? "Kunde inte spara"}: ${errMsg || (L.unknownError ?? "Okänt fel")}`,
      );
    } finally {
      setIsUpdatingBuyerTransport(false);
    }
  };

  // Handler for DeliveryFeePopup onSave
  const handleDeliveryFeeSave = async (fees: {
    zoneA: number;
    zoneB: number;
    zoneC: number;
    zoneD: number;
  }) => {
    setDeliveryFees(fees);
    setIsUpdatingDeliveryFees(true);

    try {
      await updateListing({
        id: listingId,
        deliveryFee: fees,
      });

      toast.success(L.deliveryFeesUpdated ?? "Leveransavgifter uppdaterade!");
    } catch (error) {
      toast.error(
        L.errorUpdatingDeliveryFees ??
          "Fel vid uppdatering av leveransavgifter. Försök igen.",
      );
    } finally {
      setIsUpdatingDeliveryFees(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Pickup from Seller */}
      <Card
        className={
          sellerTransportForm.status === "completed" && "bg-green-500/20"
        }
      >
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-blue-500">
            <Building className="h-5 w-5" />
            {L.pickupFromSeller ?? "Upphämtning från säljare"}
            {sellerTransportForm.status === "completed" && (
              <Check className="h-8 w-8 text-green-500" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {sellerPickupTransports.length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{L.status ?? "Status"}</p>
                  <Badge variant="outline" className="mt-1">
                    {getTransportStatusLabel(sellerTransportForm.status)}
                  </Badge>
                </div>
                <Select
                  value={sellerTransportForm.status}
                  onValueChange={(
                    value:
                      | "not_started"
                      | "planned"
                      | "in_progress"
                      | "completed",
                  ) => {
                    setIsEditingSellerForm(true);
                    setSellerTransportForm((prev) => ({
                      ...prev,
                      status: value,
                    }));
                  }}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">
                      {L.notStarted ?? "Ej påbörjad"}
                    </SelectItem>
                    <SelectItem value="planned">
                      {L.planned ?? "Planerad"}
                    </SelectItem>
                    <SelectItem value="in_progress">
                      {L.inProgressPickup ?? "Under upphämtning"}
                    </SelectItem>
                    <SelectItem value="completed">
                      {L.pickedUp ?? "Upphämtad"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="font-medium text-blue-500">
                    {L.pickupAddress ?? "Upphämtningadress"}
                  </p>

                  {listing?.listing?.status === "completed" && (
                    <div className="space-y-2">
                      {isLoadingSellerAddresses ? (
                        <p className="text-xs text-muted-foreground">
                          {L.loading ?? "Laddar..."}
                        </p>
                      ) : sellerAddresses.length === 0 ? (
                        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                          {L.noSellerDeliveryAddress ??
                            "Säljaren har ingen sparad leveransadress i sitt konto."}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">
                            {L.selectSellerAddress ?? "Välj säljarens adress"}
                          </p>
                          <Select
                            value={
                              sellerAddresses.find(
                                (a) =>
                                  formatDeliveryAddress(a) ===
                                  sellerTransportForm.fromAddress,
                              )?.id || ""
                            }
                            onValueChange={(id) => {
                              const selected = sellerAddresses.find(
                                (a) => a.id === id,
                              );
                              if (!selected) return;
                              setIsEditingSellerForm(true);
                              setSellerTransportForm((prev) => ({
                                ...prev,
                                fromAddress: formatDeliveryAddress(selected),
                              }));
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={L.selectAddress ?? "Välj adress"}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {sellerAddresses.map((a) => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.isDefault ? "⭐ " : ""}
                                  {formatDeliveryAddress(a)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  <Input
                    value={sellerTransportForm.fromAddress}
                    onChange={(e) => {
                      setIsEditingSellerForm(true);
                      setSellerTransportForm((prev) => ({
                        ...prev,
                        fromAddress: e.target.value,
                      }));
                    }}
                    placeholder={
                      L.sellerAddressPlaceholder ?? "Säljarens adress"
                    }
                    disabled={sellerTransportForm.status === "completed"}
                    className="min-w-0 break-words"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-blue-500">
                  {L.pickupDate ?? "Upphämtningsdatum"}
                </p>
                <DateTimePicker
                  value={sellerTransportForm.scheduledDate || ""}
                  onChange={(value) => {
                    setIsEditingSellerForm(true);
                    setSellerTransportForm((prev) => ({
                      ...prev,
                      scheduledDate: value,
                    }));
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sellerPickupComment">
                  {L.comment ?? "Kommentar"}
                </Label>
                <Textarea
                  id="sellerPickupComment"
                  value={sellerTransportForm.comment}
                  onChange={(e) => {
                    setIsEditingSellerForm(true);
                    setSellerTransportForm((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }));
                  }}
                  placeholder={L.addComment ?? "Lägg till kommentar..."}
                  rows={3}
                  disabled={sellerTransportForm.status === "completed"}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSellerPickup}
                  size="sm"
                  disabled={
                    !hasSellerPickupChanges ||
                    !isSellerPickupValid ||
                    isUpdatingSellerPickup
                  }
                >
                  {isUpdatingSellerPickup
                    ? (L.saving ?? "Sparar...")
                    : (L.saveChanges ?? "Spara ändringar")}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>
                {L.noSellerPickupConfigured ??
                  "Ingen säljar-upphämtning konfigurerad än"}
              </p>
              <p className="text-sm">
                {L.transportCreatedAutomatically ??
                  "Transport skapas automatiskt när annonsen skapas"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery to Buyer */}
      <Card
        className={
          buyerTransportForm.status === "completed" && "bg-green-500/20"
        }
      >
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-green-500">
            <Truck className="h-5 w-5" />
            {L.deliveryToBuyer ?? "Leverans till köpare"}
            {buyerTransportForm.status === "completed" && (
              <Check className="h-8 w-8 text-green-500" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Show different content based on vehicle status */}
          {listing?.listing?.status === "completed" ? (
            // Vehicle sold - show full delivery information
            <>
              {/* Transport Type Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">
                    {L.deliveryType ?? "Leveranstyp"}
                  </h4>
                </div>
                <Select
                  value={buyerTransportForm.transportType}
                  onValueChange={(value: "pickup" | "delivery") => {
                    setIsEditingBuyerForm(true);
                    setBuyerTransportForm((prev) => ({
                      ...prev,
                      transportType: value,
                      // Clear address when switching to pickup
                      address: value === "pickup" ? "" : prev.address,
                    }));
                    setHasManuallySelectedType(true);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={L.selectDeliveryType ?? "Välj leveranstyp"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">
                      {L.pickupAtBilenia ?? "Upphämtning från kund"}
                    </SelectItem>
                    <SelectItem value="delivery">
                      {L.homeDelivery ?? "Hemleverans"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Unified Buyer Transport Form */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium text-green-500">
                  {buyerTransportForm.transportType === "pickup"
                    ? (L.pickupAtBilenia ?? "Upphämtning från kund")
                    : (L.homeDelivery ?? "Hemleverans")}
                </h4>

                {/* Status högst upp */}
                <div className="flex flex-row items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-green-500">Status</p>
                      {buyerTransportForm.status === "planned" && (
                        <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700">
                          <Check className="h-3 w-3 mr-1" />
                          {L.bookingConfirmed ?? "Bokning bekräftad"}
                        </Badge>
                      )}
                      {buyerTransportForm.status === "requested" && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700">
                          {L.bookingRequest ?? "Bokningsförfrågan"}
                        </Badge>
                      )}
                    </div>
                    <Select
                      value={buyerTransportForm.status}
                      onValueChange={(
                        value:
                          | "not_started"
                          | "requested"
                          | "planned"
                          | "in_progress"
                          | "completed",
                      ) => {
                        setIsEditingBuyerForm(true);
                        setBuyerTransportForm((prev) => ({
                          ...prev,
                          status: value,
                        }));
                      }}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">
                          {L.notStarted ?? "Ej påbörjad"}
                        </SelectItem>
                        <SelectItem value="requested">
                          {L.requested ?? "Begärd"}
                        </SelectItem>
                        <SelectItem value="planned">
                          {L.planned ?? "Planerad"}
                        </SelectItem>
                        <SelectItem value="in_progress">
                          {buyerTransportForm.transportType === "pickup"
                            ? (L.inProgressPickup ?? "Under upphämtning")
                            : (L.inProgressDelivery ?? "Under leverans")}
                        </SelectItem>
                        <SelectItem value="completed">
                          {buyerTransportForm.transportType === "pickup"
                            ? (L.pickedUp ?? "Upphämtad")
                            : (L.delivered ?? "Levererad")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Address field - only show for delivery */}
                  {buyerTransportForm.transportType === "delivery" ? (
                    <div className="space-y-2 w-full">
                      <p className="font-medium text-green-500">
                        {L.deliveryAddress ?? "Leveransadress"}
                      </p>

                      {listing?.listing?.status === "completed" && (
                        <div className="space-y-2">
                          {isLoadingBuyerAddresses ? (
                            <p className="text-xs text-muted-foreground">
                              {L.loading ?? "Laddar..."}
                            </p>
                          ) : buyerAddresses.length === 0 ? (
                            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                              {L.noBuyerDeliveryAddress ??
                                "Köparen har ingen sparad leveransadress i sitt konto."}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">
                                {L.selectBuyerAddress ?? "Välj köparens adress"}
                              </p>
                              <Select
                                value={
                                  buyerAddresses.find(
                                    (a) =>
                                      formatDeliveryAddress(a) ===
                                      buyerTransportForm.address,
                                  )?.id || ""
                                }
                                onValueChange={(id) => {
                                  const selected = buyerAddresses.find(
                                    (a) => a.id === id,
                                  );
                                  if (!selected) return;
                                  setIsEditingBuyerForm(true);
                                  setBuyerTransportForm((prev) => ({
                                    ...prev,
                                    address: formatDeliveryAddress(selected),
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue
                                    placeholder={
                                      L.selectAddress ?? "Välj adress"
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {buyerAddresses.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>
                                      {a.isDefault ? "⭐ " : ""}
                                      {formatDeliveryAddress(a)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      )}

                      <Input
                        value={buyerTransportForm.address}
                        onChange={(e) => {
                          setIsEditingBuyerForm(true);
                          setBuyerTransportForm((prev) => ({
                            ...prev,
                            address: e.target.value,
                          }));
                        }}
                        placeholder={
                          L.buyerAddressPlaceholder ?? "Köparens leveransadress"
                        }
                        disabled={buyerTransportForm.status === "completed"}
                        className="min-w-0 break-words"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2 w-full">
                      <p className="font-medium text-green-500">
                        {L.address ?? "Adress"}
                      </p>
                      <div className="p-3 border rounded-lg">
                        <p className="text-muted-foreground">
                          {isLoadingSellerAddresses
                            ? (L.loading ?? "Laddar...")
                            : resolvedSellerPickupLine.trim() ||
                              (L.sellerPickupForBuyerMissing ??
                                "Säljarens upphämtningsadress saknas. Fyll i under upphämtning från säljare eller säkerställ att säljaren har en leveransadress.")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="font-medium text-green-500">
                    {L.dateAndTime ?? "Datum och tid"}
                  </p>
                  <DateTimePicker
                    value={buyerTransportForm.scheduledDate || ""}
                    onChange={(value) => {
                      setIsEditingBuyerForm(true);
                      setBuyerTransportForm((prev) => ({
                        ...prev,
                        scheduledDate: value,
                      }));
                    }}
                    minDaysAhead={1}
                  />
                  <p className="text-xs text-muted-foreground">
                    {L.deliveryDateMin1Day ??
                      "Leveransdatum måste vara minst 1 dag framåt. Detta är ett bokningsönskemål. Om tiden passar BILENIA kommer vi att kontakta köparen och bekräfta bokningen. Om tiden inte passar kommer vi också att kontakta köparen och föreslå mer passande tider."}
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="font-medium text-green-500">
                    {L.comment ?? "Kommentar"}
                  </p>
                  <Textarea
                    placeholder={
                      buyerTransportForm.transportType === "pickup"
                        ? (L.addCommentPickup ??
                          "Lägg till kommentar för upphämtning...")
                        : (L.addCommentDelivery ??
                          "Lägg till kommentar för leverans...")
                    }
                    value={buyerTransportForm.comment}
                    onChange={(e) => {
                      setIsEditingBuyerForm(true);
                      setBuyerTransportForm((prev) => ({
                        ...prev,
                        comment: e.target.value,
                      }));
                    }}
                    className="min-h-[60px]"
                    disabled={buyerTransportForm.status === "completed"}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Reset to original values
                      if (buyerTransport) {
                        setBuyerTransportForm({
                          transportType:
                            buyerTransport.type === "buyer_pickup"
                              ? "pickup"
                              : "delivery",
                          status: buyerTransport.status,
                          comment: buyerTransport.comment || "",
                          scheduledDate: buyerTransport.scheduledDate || "",
                          address:
                            buyerTransport.type === "buyer_delivery"
                              ? buyerTransport.toAddress || ""
                              : "",
                        });
                        setIsEditingBuyerForm(false);
                      }
                    }}
                  >
                    {L.cancel ?? "Avbryt"}
                  </Button>
                  <Button
                    onClick={handleSaveBuyerTransport}
                    disabled={
                      !hasBuyerTransportChanges ||
                      !isBuyerTransportValid ||
                      isUpdatingBuyerTransport
                    }
                  >
                    {isUpdatingBuyerTransport
                      ? (L.saving ?? "Sparar...")
                      : buyerTransport
                        ? (L.saveChanges ?? "Spara ändringar")
                        : (L.bookCustomerTransport ?? "Boka kundtransport")}
                  </Button>
                </div>

                {/* Confirm Booking Button - Show when status is REQUESTED */}
                {buyerTransport && buyerTransport.status === "requested" && (
                  <div className="border-t pt-4">
                    <Button
                      onClick={async () => {
                        try {
                          await confirmBuyerDeliveryAsync(listingId);
                          await refetchListingDetails();
                          toast.success(
                            L.bookingConfirmedBuyerNotified ??
                              "Bokning bekräftad! Köparen har fått meddelande.",
                          );
                        } catch (error: unknown) {
                          const errMsg =
                            error instanceof Error
                              ? error.message
                              : typeof error === "object" &&
                                  error !== null &&
                                  "message" in error &&
                                  typeof (error as { message: unknown })
                                    .message === "string"
                                ? (error as { message: string }).message
                                : "";
                          toast.error(
                            `${L.errorConfirmingDelivery ?? "Kunde inte bekräfta bokning"}: ${errMsg || "Okänt fel"}`,
                          );
                        }
                      }}
                      disabled={isConfirmingBuyerDelivery}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isConfirmingBuyerDelivery
                        ? (L.saving ?? "Bekräftar...")
                        : (L.confirmBooking ?? "Bekräfta bokning")}
                    </Button>
                  </div>
                )}

                {/* Delivery Fees Button - Show when delivery is selected */}
                {buyerTransportForm.transportType === "delivery" && (
                  <div className="border-t pt-4">
                    <div className="w-full">
                      <DeliveryFeePopup
                        deliveryFee={deliveryFees}
                        listingId={listingId}
                        isAdmin={true}
                        onSave={handleDeliveryFeeSave}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Vehicle not sold - only show delivery fees button
            <div className="flex flex-col justify-center items-center">
              <p className="text-muted-foreground mb-4">
                Leveransinformation visas när fordonet är sålt
              </p>
              <DeliveryFeePopup
                deliveryFee={deliveryFees}
                listingId={listingId}
                isAdmin={true}
                onSave={handleDeliveryFeeSave}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
